import { Controller } from "@hotwired/stimulus";

// Registered on an ancestor of both the bulk-reject bar and the leads table, because the row
// checkboxes and the controls that act on them live in different templates.
export default class extends Controller {
  static targets = [
    "form", "checkbox", "selectPage", "count", "submit", "scope",
    "dialog", "dialogMessage", "overlay", "overlayMessage",
  ];

  connect () {
    this.refresh();
    this.hidePending();

    // Coming back via the browser's back button can restore this page from cache with the overlay
    // still up, since Stimulus does not reconnect for a bfcache restore.
    this.onPageShow = () => this.hidePending();
    window.addEventListener("pageshow", this.onPageShow);
  }

  disconnect () {
    window.removeEventListener("pageshow", this.onPageShow);
  }

  togglePage (event) {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = event.target.checked;
    });
    this.refresh();
  }

  refresh () {
    const selected = this.selectedCheckboxes().length;

    if (this.hasCountTarget) {
      this.countTarget.textContent = `${selected} selected`;
    }

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = selected === 0;
    }

    if (this.hasSelectPageTarget) {
      const total = this.checkboxTargets.length;
      this.selectPageTarget.checked = total > 0 && selected === total;
      this.selectPageTarget.indeterminate = selected > 0 && selected < total;
    }
  }

  // "Reject selected" acts on the ticked rows only, so the count in the prompt is the page's
  // selection rather than the group total.
  confirmSelected (event) {
    const selected = this.selectedCheckboxes().length;
    if (selected === 0) return;

    this.scopeTarget.value = "selected";

    const label = event.currentTarget.dataset.groupLabel;
    this.ask(`Reject ${selected} ${this.leads(selected)} from ${label}? ` +
      "Each one can be reactivated individually afterwards.");
  }

  // The group spans more pages than one, so this deliberately ignores the ticked rows and tells the
  // server to resolve the whole group itself.
  confirmGroup (event) {
    this.scopeTarget.value = "group";
    this.ask(event.currentTarget.dataset.confirmation);
  }

  ask (message) {
    this.dialogMessageTarget.textContent = message;
    this.dialogTarget.show();
  }

  cancel () {
    this.dialogTarget.hide();
  }

  // requestSubmit, not the raw form.submit(): submit() skips the submit event, and the overlay
  // hangs off that event precisely so it can never suppress the submission it reports on.
  submit () {
    this.dialogTarget.hide();
    this.formTarget.requestSubmit();
  }

  // The request takes seconds on a large group. Navigating away does not stop the rejections — the
  // server runs to completion — but the response carrying the flash is discarded, so the reviewer
  // would see no confirmation and reasonably conclude nothing happened. Covering the page keeps
  // them here until it lands, and the overlay covering the viewport rules out a second submit.
  //
  // Bound to the form's `submit`, deliberately, rather than either button's `click`: by then the
  // submission is already under way, so nothing done here can suppress it. Touching the buttons
  // from a click handler could — a disabled button cannot be a form's submitter, and the browser
  // silently abandons the submission, leaving the spinner over a request that was never sent.
  showPending () {
    const { groupCount, groupLabel } = this.formTarget.dataset;
    const count = this.scopeTarget.value === "group"
      ? Number(groupCount)
      : this.selectedCheckboxes().length;

    if (this.hasOverlayMessageTarget) {
      this.overlayMessageTarget.textContent = `Rejecting ${count} ${this.leads(count)} from ${groupLabel}…`;
    }

    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("hidden");
      this.overlayTarget.classList.add("flex");
    }
  }

  hidePending () {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("hidden");
      this.overlayTarget.classList.remove("flex");
    }
  }

  selectedCheckboxes () {
    return this.checkboxTargets.filter((checkbox) => checkbox.checked);
  }

  leads (count) {
    return count === 1 ? "lead" : "leads";
  }
}
